import { useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import LoadingDots from '../components/LoadingDots'
import { signInWithGoogle, signInWithEmail, signUpWithEmail, requestPasswordReset, verifyGoogleOAuthAllowlist } from '../lib/api'
import { supabase } from '../lib/supabase'

type Mode = 'signin' | 'signup' | 'forgot'

export default function Login() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const next = searchParams.get('next')

  const [checking, setChecking] = useState(true)
  const [mode, setMode] = useState<Mode>('signin')

  const emailRef = useRef<HTMLInputElement>(null)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      if (data.user) {
        try {
          await verifyGoogleOAuthAllowlist()
          navigate(next ?? '/manage', { replace: true })
        } catch {
          await supabase.auth.signOut()
          setChecking(false)
        }
      } else {
        setChecking(false)
      }
    })
  }, [navigate, next])

  // The browser (Chrome's password manager) auto-fills and focuses the email
  // field on load, selecting its text. Release that focus so the login page
  // doesn't open with the first input highlighted (or a keyboard on mobile).
  useEffect(() => {
    const el = emailRef.current
    if (el && document.activeElement === el) {
      el.blur()
      el.setSelectionRange(0, 0)
    }
  }, [])

  const handleGoogle = () => {
    const redirectTo = next
      ? `${window.location.origin}${next}`
      : `${window.location.origin}/manage`
    signInWithGoogle(redirectTo)
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (mode === 'signup' && password !== confirmPassword) {
      setError('Passordene stemmer ikke overens.')
      return
    }

    setSubmitting(true)
    try {
      if (mode === 'forgot') {
        await requestPasswordReset(email)
        setConfirmed(true)
      } else if (mode === 'signup') {
        const { needsConfirmation } = await signUpWithEmail(email, password)
        if (needsConfirmation) {
          setConfirmed(true)
        } else {
          // Confirmation disabled — user is signed in immediately
          navigate(next ?? '/manage', { replace: true })
        }
      } else {
        await signInWithEmail(email, password)
        navigate(next ?? '/manage', { replace: true })
      }
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  const switchMode = (m: Mode) => {
    setMode(m)
    setError(null)
    setConfirmed(false)
    setPassword('')
    setConfirmPassword('')
  }

  if (checking) {
    return <div className="min-h-dvh bg-gray-950" />
  }

  return (
    <div className="min-h-dvh bg-gray-950 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm flex flex-col gap-8">
        <h1 className="text-white text-4xl font-bold tracking-tight text-center">h3r</h1>

        {/* Google OAuth */}
        {mode !== 'forgot' && (
          <>
            <button
              onClick={handleGoogle}
              className="
                flex items-center justify-center gap-3
                bg-white text-gray-800
                px-6 py-3 rounded-full
                text-base font-medium
                shadow-lg hover:shadow-xl hover:bg-gray-50
                transition-all duration-200 active:scale-95
              "
            >
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Logg inn med Google
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-800" />
              <span className="text-xs text-gray-600 uppercase tracking-wider">eller</span>
              <div className="flex-1 h-px bg-gray-800" />
            </div>
          </>
        )}

        {mode === 'forgot' && (
          <p className="text-sm text-gray-400 text-center">
            Skriv inn e-postadressen din, så sender vi deg en lenke for å tilbakestille passordet.
          </p>
        )}

        {/* Mode tabs */}
        {mode !== 'forgot' && (
          <div className="flex rounded-lg bg-gray-900 border border-gray-800 p-1 gap-1">
            {(['signin', 'signup'] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => switchMode(m)}
                className={`
                  flex-1 py-1.5 rounded-md text-sm font-medium transition-colors duration-150
                  ${mode === m
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-500 hover:text-gray-300'}
                `}
              >
                {m === 'signin' ? 'Logg inn' : 'Registrer deg'}
              </button>
            ))}
          </div>
        )}

        {/* Confirmation state */}
        {confirmed ? (
          <div className="flex flex-col gap-4">
            <div className="rounded-xl bg-green-900 border border-green-700 px-5 py-4 text-green-200 text-sm text-center leading-relaxed">
              {mode === 'forgot'
                ? 'Hvis e-postadressen er registrert, har vi sendt deg en lenke for tilbakestilling av passord.'
                : 'Sjekk innboksen din og bekreft e-postadressen din for å fullføre registreringen.'}
            </div>
            {mode === 'forgot' && (
              <button
                type="button"
                onClick={() => switchMode('signin')}
                className="text-sm text-blue-400 hover:text-blue-300 text-center transition-colors"
              >
                Tilbake til innlogging
              </button>
            )}
          </div>
        ) : (
          <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-sm text-gray-400">E-post</label>
              <input
                id="email"
                ref={emailRef}
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="
                  bg-gray-900 border border-gray-700 rounded-lg
                  px-3 py-2.5 text-sm text-white
                  focus:outline-none focus:border-blue-500
                  placeholder-gray-600
                "
                placeholder="deg@example.com"
              />
            </div>

            {mode !== 'forgot' && (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-sm text-gray-400">Passord</label>
                  {mode === 'signin' && (
                    <button
                      type="button"
                      onClick={() => switchMode('forgot')}
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Glemt passord?
                    </button>
                  )}
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="
                    bg-gray-900 border border-gray-700 rounded-lg
                    px-3 py-2.5 text-base text-white
                    focus:outline-none focus:border-blue-500
                    placeholder-gray-600
                  "
                  placeholder="Minst 8 tegn"
                />
              </div>
            )}

            {mode === 'signup' && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="confirm-password" className="text-sm text-gray-400">Bekreft passord</label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="
                    bg-gray-900 border border-gray-700 rounded-lg
                    px-3 py-2.5 text-base text-white
                    focus:outline-none focus:border-blue-500
                    placeholder-gray-600
                  "
                  placeholder="Gjenta passordet"
                />
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-red-900 border border-red-700 px-4 py-3 text-red-200 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="
                flex items-center justify-center gap-2
                bg-blue-600 hover:bg-blue-500
                disabled:bg-gray-700 disabled:cursor-not-allowed
                text-white text-sm font-medium
                px-4 py-2.5 rounded-lg
                transition-colors duration-200 active:scale-95
              "
            >
              {submitting && (
                <LoadingDots />
              )}
              {mode === 'signin' ? 'Logg inn' : mode === 'signup' ? 'Opprett konto' : 'Send tilbakestillingslenke'}
            </button>

            {mode === 'forgot' && (
              <button
                type="button"
                onClick={() => switchMode('signin')}
                className="text-sm text-blue-400 hover:text-blue-300 text-center transition-colors"
              >
                Tilbake til innlogging
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  )
}
